import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  type Firestore,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { runtimeEnv } from './runtime-env';

export const googleProvider = new GoogleAuthProvider();

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

type EmulatorConnectionState = {
  auth?: boolean;
  firestore?: boolean;
};

type DevWindow = Window & {
  pokerDb?: {
    db: Firestore;
    doc: typeof doc;
    setDoc: typeof setDoc;
    serverTimestamp: typeof serverTimestamp;
  };
};

const emulatorState = globalThis as typeof globalThis & {
  __pokerKnightsFirebaseEmulators?: EmulatorConnectionState;
};

function envNumber(name: keyof ImportMetaEnv, fallback: number): number {
  const value = Number.parseInt(import.meta.env[name] ?? '', 10);
  return Number.isFinite(value) ? value : fallback;
}

function connectLocalEmulators(firestore: Firestore, authInstance: Auth) {
  if (!runtimeEnv.usesFirebaseEmulators) {
    return;
  }

  const state = emulatorState.__pokerKnightsFirebaseEmulators ??= {};
  const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || '127.0.0.1';
  const firestorePort = envNumber('VITE_FIRESTORE_EMULATOR_PORT', 8085);
  const authUrl =
    import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL || 'http://127.0.0.1:9099';

  if (!state.firestore) {
    connectFirestoreEmulator(firestore, firestoreHost, firestorePort);
    state.firestore = true;
  }

  if (!state.auth) {
    connectAuthEmulator(authInstance, authUrl, {
      disableWarnings: runtimeEnv.isTest,
    });
    state.auth = true;
  }
}

let app: FirebaseApp;
let db: Firestore;

if (getApps().length) {
  app = getApp();
  db = getFirestore(app);
} else {
  app = initializeApp(firebaseConfig);
  db = initializeFirestore(app, {
    cacheSizeBytes: CACHE_SIZE_UNLIMITED,
    experimentalForceLongPolling: runtimeEnv.usesFirebaseEmulators,
  });
}

export const auth = getAuth(app);
if (runtimeEnv.configurationErrors.length > 0) {
  const message = runtimeEnv.configurationErrors.join(' ');
  if (import.meta.env.MODE === 'test') {
    throw new Error(message);
  }
  console.error(`[runtime-env] ${message}`);
}
connectLocalEmulators(db, auth);

if (typeof window !== 'undefined' && runtimeEnv.isNonProduction) {
  (window as DevWindow).pokerDb = {
    db,
    doc,
    setDoc,
    serverTimestamp,
  };
}

export { db };
export const storage = getStorage(app);

